import {
  ALLSTATE_DEFAULT_DESCRIPTION,
  buildAllstateServiceSchema,
} from '@core-mkt/services/schema/allstate-roadside.schema';
import { stripHtml } from '@core-mkt/services/schema/faq-schema.util';
import type { SchemaOrgNode } from '@core-mkt/services/schema/schema-org-node.model';

/**
 * Stable `Service.name` for the `rsa-product` upsell. The rendered title is
 * built dynamically from the discount percentage (resolved async from the xGrit
 * API), so schema uses a stable product-independent name rather than the
 * marketing headline.
 */
const RSA_PRODUCT_SERVICE_NAME = 'Allstate Roadside Services';

/**
 * Course `@id` prefix — mirrors the urn the page-data resolver assigns to each
 * xGrit Course node (`urn:xgrit:course:{productId}`), so `isRelatedTo` resolves
 * to the Course already on the graph.
 */
const COURSE_ID_PREFIX = 'urn:xgrit:course:';

export interface RsaProductServiceSchemaData {
  /** Craft `rsaTitle` (raw HTML or Angular SafeHtml). Optional — falls back to a stable name. */
  title?: unknown;
  /** Craft `rsaDescription`; falls back to the shared Allstate default. */
  description?: unknown;
  /** xGrit product id (`craftData.xgritProductId`) for the related Course reference. */
  productId?: string | number | null;
}

/**
 * `Service` schema for the `rsa-product` product-page upsell. Same Allstate
 * provider as `rsa` (EPS-3923 AC2) and, when the page has a valid xGrit
 * product, an `isRelatedTo` reference to that product's Course node so search
 * engines see the upsell relationship. Pure function — no DOM, no DI, SSR-safe.
 */
export function getRsaProductServiceSchema(
  data: RsaProductServiceSchemaData | null | undefined,
  pageUrl: string,
): SchemaOrgNode[] | null {
  if (!pageUrl) return null;
  const name = stripHtml(data?.title) || RSA_PRODUCT_SERVICE_NAME;
  const description = stripHtml(data?.description) || stripHtml(ALLSTATE_DEFAULT_DESCRIPTION);
  const productId = data?.productId;
  const hasProductId = productId != null && `${productId}`.length > 0;
  const relatedCourseId = hasProductId ? `${COURSE_ID_PREFIX}${productId}` : null;
  return buildAllstateServiceSchema(pageUrl, name, description, relatedCourseId);
}
